/**
 * Property details schema — seller-friendly sections that map to mls_profile JSON.
 * Organized the way homeowners think about their property, not MLS data entry.
 */
import {
  ANSWER_NO,
  ANSWER_NOT_SURE,
  ANSWER_YES,
  CONDOMINIUM_UNIT_COOPERATIVE,
  LAND_BUILDINGS_DWELLING,
  LAND_BUILDINGS_NONE,
  LAND_BUILDINGS_OUTBUILDINGS_ONLY,
  MOBILE_HOME_LAND_LEASED,
  MOBILE_HOME_LAND_OWNED,
  MOBILE_HOME_TITLE_NONE,
  MOBILE_HOME_TITLE_OUTSTANDING,
  PROPERTY_TYPE_CONDO,
  PROPERTY_TYPE_LAND,
  PROPERTY_TYPE_MOBILE_HOME,
  PROPERTY_TYPE_MULTI_FAMILY,
  PROPERTY_TYPE_SINGLE_FAMILY,
  PROPERTY_TYPE_TOWNHOUSE,
  normalizePropertyType,
  normalizeQualifiers,
} from './paperworkCategory';
import { copyText } from './paperworkCopy';

/**
 * Property type options. The labels are stored data (`mls_profile.property_type`
 * keeps the label), so existing labels never change; Co-op, Time-Share and
 * Mixed-Use are always blocked (legal copy §1).
 */
export const PROPERTY_TYPE_OPTIONS = [
  '',
  'Single Family',
  'Condo',
  'Townhouse',
  'Multi-Family',
  'Mobile Home',
  'Land',
  copyText('WI.paperwork.q.property_type.option.cooperative'),
  copyText('WI.paperwork.q.property_type.option.timeshare'),
  copyText('WI.paperwork.q.property_type.option.mixed_use'),
];

/** One-line hints under the property type select (canonical type → copy key). */
export const PROPERTY_TYPE_HINT_KEYS = {
  multi_family: 'WI.paperwork.q.property_type.hint.multi_family',
  land: 'WI.paperwork.q.property_type.hint.land',
  cooperative: 'WI.paperwork.q.property_type.hint.cooperative',
};

const q = (key, suffix) => `WI.paperwork.q.${key}.${suffix}`;
const option = (key, value) => ({ value, labelKey: q(key, `option.${value}`) });

/**
 * The paperwork questions (design §3.2), in the order they are asked. Each is
 * shown only when `showIf(propertyType, answers)` holds, with the canonical
 * property type and normalized answers. Values are canonical codes; labels are
 * legal's factual wording (copy keys, resolved by the component).
 */
export const PAPERWORK_QUALIFIER_FIELDS = [
  {
    key: 'condominium_unit',
    type: 'radio',
    labelKey: (ptype) =>
      ptype === PROPERTY_TYPE_LAND
        ? q('condominium_unit', 'label.land')
        : ptype === PROPERTY_TYPE_CONDO
          ? q('condominium_unit', 'label.condo')
          : q('condominium_unit', 'label'),
    helpKey: (ptype) =>
      ptype === PROPERTY_TYPE_CONDO ? q('condominium_unit', 'help.condo') : q('condominium_unit', 'help'),
    options: (ptype) => [
      option('condominium_unit', ANSWER_YES),
      option('condominium_unit', ANSWER_NO),
      option('condominium_unit', ANSWER_NOT_SURE),
      // Legal Rule 1: a co-op listed as a condo is caught here.
      ...(ptype === PROPERTY_TYPE_CONDO ? [option('condominium_unit', CONDOMINIUM_UNIT_COOPERATIVE)] : []),
    ],
    showIf: (ptype) =>
      [PROPERTY_TYPE_SINGLE_FAMILY, PROPERTY_TYPE_TOWNHOUSE, PROPERTY_TYPE_LAND, PROPERTY_TYPE_CONDO].includes(ptype),
  },
  {
    key: 'mobile_home_land',
    type: 'radio',
    labelKey: () => q('mobile_home_land', 'label'),
    helpKey: () => q('mobile_home_land', 'help'),
    options: () => [
      option('mobile_home_land', MOBILE_HOME_LAND_OWNED),
      option('mobile_home_land', MOBILE_HOME_LAND_LEASED),
    ],
    showIf: (ptype) => ptype === PROPERTY_TYPE_MOBILE_HOME,
  },
  {
    key: 'mobile_home_affixed',
    type: 'radio',
    labelKey: () => q('mobile_home_affixed', 'label'),
    helpKey: () => q('mobile_home_affixed', 'help'),
    options: () => [
      option('mobile_home_affixed', ANSWER_YES),
      option('mobile_home_affixed', ANSWER_NO),
      option('mobile_home_affixed', ANSWER_NOT_SURE),
    ],
    showIf: (ptype, a) => ptype === PROPERTY_TYPE_MOBILE_HOME && a.mobile_home_land === MOBILE_HOME_LAND_OWNED,
  },
  {
    key: 'mobile_home_title',
    type: 'radio',
    labelKey: () => q('mobile_home_title', 'label'),
    helpKey: () => q('mobile_home_title', 'help'),
    options: () => [
      option('mobile_home_title', MOBILE_HOME_TITLE_NONE),
      option('mobile_home_title', MOBILE_HOME_TITLE_OUTSTANDING),
      option('mobile_home_title', ANSWER_NOT_SURE),
    ],
    showIf: (ptype, a) =>
      ptype === PROPERTY_TYPE_MOBILE_HOME &&
      a.mobile_home_land === MOBILE_HOME_LAND_OWNED &&
      a.mobile_home_affixed === ANSWER_YES,
  },
  // Commercial space before the unit count: that's the order the rules
  // apply them in, so the seller is never asked to fix them out of order.
  {
    key: 'has_commercial_space',
    type: 'radio',
    labelKey: () => q('has_commercial_space', 'label'),
    helpKey: () => q('has_commercial_space', 'help'),
    options: () => [option('has_commercial_space', ANSWER_YES), option('has_commercial_space', ANSWER_NO)],
    showIf: (ptype) => ptype === PROPERTY_TYPE_MULTI_FAMILY,
  },
  {
    key: 'unit_count',
    type: 'number',
    labelKey: () => q('unit_count', 'label'),
    helpKey: () => q('unit_count', 'help'),
    placeholderKey: q('unit_count', 'placeholder'),
    invalidKey: q('unit_count', 'invalid'),
    showIf: (ptype) => ptype === PROPERTY_TYPE_MULTI_FAMILY,
  },
  {
    key: 'land_buildings',
    type: 'radio',
    labelKey: () => q('land_buildings', 'label'),
    helpKey: () => q('land_buildings', 'help'),
    options: () => [
      option('land_buildings', LAND_BUILDINGS_NONE),
      option('land_buildings', LAND_BUILDINGS_OUTBUILDINGS_ONLY),
      option('land_buildings', LAND_BUILDINGS_DWELLING),
    ],
    showIf: (ptype, a) => ptype === PROPERTY_TYPE_LAND && a.condominium_unit === ANSWER_NO,
  },
  {
    key: 'operating_farm',
    type: 'radio',
    labelKey: () => q('operating_farm', 'label'),
    helpKey: () => q('operating_farm', 'help'),
    options: () => [option('operating_farm', ANSWER_YES), option('operating_farm', ANSWER_NO)],
    showIf: (ptype, a) =>
      ptype === PROPERTY_TYPE_LAND &&
      a.condominium_unit === ANSWER_NO &&
      a.land_buildings === LAND_BUILDINGS_OUTBUILDINGS_ONLY,
  },
];

/** The paperwork questions that apply to a profile right now. */
export function visiblePaperworkFields(profile) {
  const ptype = normalizePropertyType(profile?.property_type);
  const answers = normalizeQualifiers(profile);
  return PAPERWORK_QUALIFIER_FIELDS.filter((field) => field.showIf(ptype, answers));
}

/**
 * The profile after one paperwork answer (or property type) changes, with
 * every question that no longer applies cleared (design §9.2). Cleared keys
 * are set to '' so the save payload drops them.
 */
const clearHiddenAnswers = (profile, rounds) => {
  const visible = new Set(visiblePaperworkFields(profile).map((f) => f.key));
  const stale = PAPERWORK_QUALIFIER_FIELDS.filter(
    (f) => !visible.has(f.key) && profile[f.key] !== undefined && profile[f.key] !== '',
  );
  if (stale.length === 0 || rounds <= 0) return profile;
  const cleared = { ...profile, ...Object.fromEntries(stale.map((f) => [f.key, ''])) };
  // A cleared answer can hide a question below it, so settle until stable.
  return clearHiddenAnswers(cleared, rounds - 1);
};

export function applyPaperworkAnswer(profile, key, value) {
  return clearHiddenAnswers({ ...(profile || {}), [key]: value }, PAPERWORK_QUALIFIER_FIELDS.length);
}

// The "None" chip on multi-selects, so a seller can answer a required
// question whose honest answer is "nothing".
export const NONE_OPTION = 'None';

const noHoa = (profile) => !profile.hoa || profile.hoa === 'None';

// Every Property Features field is required (fill in what you know, and
// "None" where nothing applies), except where a field can't apply:
//   - `optional: true`            never required
//   - `optionalWhen(profile)`     not required when it returns true
//   - `alternative: '<key>'`      either this field or that one will do
//   - section `notForLand: true`  not required on vacant land (no building)
export const MLS_SECTIONS = [
  {
    key: 'construction',
    notForLand: true,
    title: 'Construction & Structure',
    icon: '🏗️',
    fields: [
      { key: 'year_built', type: 'number', label: 'Year Built', placeholder: '1998' },
      // property_type lives in the visible Property type block
      // (PropertyTypeFields), not in this collapsed section: it picks the
      // listing's paperwork.
      { key: 'stories', type: 'select', label: 'Stories', options: ['', '1', '1.5', '2', '2.5', '3', '3+'] },
      // MLS boards take a primary and a secondary exterior (e.g. brick front,
      // vinyl sides). Drafts saved before this held a single string.
      { key: 'construction_material', type: 'multiselect', label: 'Exterior Material', maxSelections: 2, options: ['Brick', 'Vinyl Siding', 'Wood', 'Stucco', 'Stone', 'Fiber Cement', 'Metal', 'Other'] },
      { key: 'roof_type', type: 'select', label: 'Roof', options: ['', 'Asphalt Shingle', 'Metal', 'Tile', 'Slate', 'Flat/Built-Up', 'Wood Shake', 'Other'] },
      { key: 'foundation', type: 'select', label: 'Foundation', options: ['', 'Slab', 'Crawlspace', 'Full Basement', 'Partial Basement', 'Pier & Beam', 'Other'] },
    ],
  },
  {
    key: 'interior',
    notForLand: true,
    title: 'Interior Features',
    icon: '🛋️',
    fields: [
      { key: 'total_rooms', type: 'number', label: 'Total Rooms', placeholder: '8' },
      { key: 'flooring', type: 'multiselect', label: 'Flooring', options: ['Hardwood', 'Carpet', 'Tile', 'Laminate', 'Vinyl', 'Concrete', 'Marble', NONE_OPTION] },
      { key: 'fireplace', type: 'select', label: 'Fireplace', options: ['', 'None', '1', '2', '3+'] },
      { key: 'laundry', type: 'select', label: 'Laundry', options: ['', 'First Floor', 'Second Floor', 'Basement', 'In Unit', 'Shared', 'Hookups Only', 'None'] },
      { key: 'basement', type: 'select', label: 'Basement', options: ['', 'None', 'Finished', 'Partially Finished', 'Unfinished', 'Walk-Out'] },
      { key: 'appliances_included', type: 'multiselect', label: 'Appliances Included', options: ['Refrigerator', 'Dishwasher', 'Oven/Range', 'Microwave', 'Washer', 'Dryer', 'Disposal', 'Trash Compactor', NONE_OPTION] },
    ],
  },
  {
    key: 'heating_cooling',
    notForLand: true,
    title: 'Heating & Cooling',
    icon: '🌡️',
    fields: [
      { key: 'heating_type', type: 'select', label: 'Heating', options: ['', 'Forced Air', 'Radiant', 'Baseboard', 'Heat Pump', 'Boiler', 'Space Heater', 'None'] },
      { key: 'heating_fuel', type: 'select', label: 'Fuel', options: ['', 'Natural Gas', 'Electric', 'Propane', 'Oil', 'Solar', 'Wood'] },
      { key: 'cooling_type', type: 'select', label: 'Cooling', options: ['', 'Central Air', 'Window Units', 'Mini-Split', 'Evaporative', 'None'] },
      { key: 'water_heater', type: 'select', label: 'Water Heater', options: ['', 'Gas', 'Electric', 'Tankless', 'Solar', 'Heat Pump'] },
    ],
  },
  {
    key: 'exterior',
    title: 'Exterior & Lot',
    icon: '🌳',
    fields: [
      { key: 'lot_size_sqft', type: 'number', label: 'Lot Size (sqft)', alternative: 'lot_size_acres', placeholder: '8500' },
      { key: 'lot_size_acres', type: 'number', label: 'Lot Size (acres)', alternative: 'lot_size_sqft', placeholder: '0.19', step: '0.01' },
      { key: 'fencing', type: 'select', label: 'Fencing', options: ['', 'None', 'Full', 'Partial', 'Privacy', 'Chain Link', 'Wood', 'Wrought Iron'] },
      { key: 'pool', type: 'select', label: 'Pool', options: ['', 'None', 'In-Ground', 'Above-Ground', 'Indoor', 'Community'] },
      { key: 'exterior_features', type: 'multiselect', label: 'Outdoor Features', options: ['Patio', 'Deck', 'Porch', 'Balcony', 'Sprinklers', 'Outdoor Kitchen', 'Fire Pit', 'Shed', 'Workshop', NONE_OPTION] },
    ],
  },
  {
    key: 'parking',
    notForLand: true,
    title: 'Parking & Garage',
    icon: '🚗',
    fields: [
      { key: 'garage_type', type: 'select', label: 'Garage', options: ['', 'None', 'Attached', 'Detached', 'Carport', 'Built-In'] },
      { key: 'garage_spaces', type: 'select', optionalWhen: (p) => p.garage_type === 'None', label: 'Garage Spaces', options: ['', 'None', '1 Car', '2 Car', '3 Car', '4+ Cars'] },
      { key: 'driveway', type: 'select', label: 'Driveway', options: ['', 'None', 'Asphalt', 'Concrete', 'Gravel', 'Paver', 'Circular'] },
      { key: 'parking_spaces', type: 'number', label: 'Other Parking Spaces', placeholder: '2' },
    ],
  },
  {
    key: 'utilities',
    title: 'Utilities & Systems',
    icon: '⚡',
    fields: [
      { key: 'water_source', type: 'select', label: 'Water', options: ['', 'Municipal', 'Well', 'Shared Well', 'Other'] },
      { key: 'sewer', type: 'select', label: 'Sewer', options: ['', 'Municipal', 'Septic', 'Mound', 'Holding Tank', 'Other'] },
      { key: 'electric', type: 'select', label: 'Electric', options: ['', '100 Amp', '150 Amp', '200 Amp', '400 Amp'] },
      { key: 'internet', type: 'multiselect', label: 'Internet Available', options: ['Cable', 'Fiber', 'DSL', 'Satellite', 'Fixed Wireless', NONE_OPTION] },
    ],
  },
  {
    key: 'community',
    title: 'Community & HOA',
    icon: '🏘️',
    fields: [
      { key: 'hoa', type: 'select', label: 'HOA', options: ['', 'None', 'Optional', 'Required'] },
      { key: 'hoa_fee', type: 'number', optionalWhen: noHoa, label: 'HOA Fee ($/year)', placeholder: '600' },
      { key: 'hoa_includes', type: 'multiselect', label: 'HOA Covers', optionalWhen: noHoa, options: ['Landscaping', 'Snow Removal', 'Trash', 'Water', 'Insurance', 'Pool', 'Gym', 'Clubhouse', NONE_OPTION] },
      { key: 'subdivision', type: 'text', label: 'Subdivision / Neighborhood', placeholder: 'Willow Creek Estates' },
      { key: 'school_district', type: 'text', label: 'School District', placeholder: 'Springfield USD' },
    ],
  },
  {
    key: 'taxes',
    title: 'Taxes & Costs',
    icon: '💰',
    fields: [
      { key: 'annual_taxes', type: 'number', label: 'Annual Property Tax ($)', placeholder: '4500' },
      { key: 'tax_year', type: 'number', label: 'Tax Year', placeholder: '2025' },
      { key: 'tax_id', type: 'text', label: 'Tax Parcel / PIN', placeholder: 'Enter parcel number' },
      { key: 'zoning', type: 'text', label: 'Zoning', placeholder: 'Residential' },
      { key: 'flood_zone', type: 'select', label: 'Flood Zone', options: ['', 'No', 'Yes', 'Partial', 'Unknown'] },
    ],
  },
  {
    key: 'highlights',
    title: 'Highlights & Upgrades',
    icon: '✨',
    fields: [
      { key: 'recent_upgrades', type: 'textarea', label: 'Recent Upgrades & Renovations', placeholder: 'New roof (2024), remodeled kitchen (2023), updated HVAC...' },
      { key: 'special_features', type: 'textarea', label: 'Special Features', placeholder: 'Home office, wine cellar, walk-in closets, smart home system...' },
      { key: 'inclusions', type: 'textarea', label: 'What\'s Included in the Sale', placeholder: 'All appliances, window treatments, light fixtures...' },
      { key: 'exclusions', type: 'textarea', optional: true, label: 'What\'s Excluded', placeholder: 'Dining room chandelier, backyard swing set...' },
    ],
  },
];

export const createInitialMlsProfile = () => {
  // Qualifier answers are added only when the seller answers them.
  const profile = { property_type: '' };
  MLS_SECTIONS.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.type === 'multiselect') {
        profile[field.key] = [];
      } else {
        profile[field.key] = '';
      }
    });
  });
  return profile;
};

export const toLabel = (key) =>
  key
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const MLS_FIELDS = MLS_SECTIONS.flatMap((section) => section.fields);

/**
 * Whether a Property Features answer counts as filled in: a non-empty
 * multi-select, or a non-blank value (0 is an answer).
 */
export const isMlsValueFilled = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return String(value).trim() !== '';
};

/**
 * Bring a saved mls_profile up to the current schema: a multi-select saved as
 * a single string (Exterior Material, before it allowed two) becomes a
 * one-item list.
 */
export const normalizeMlsProfile = (profile) => {
  const next = { ...(profile || {}) };
  MLS_FIELDS.forEach((field) => {
    if (field.type !== 'multiselect') return;
    const value = next[field.key];
    if (typeof value === 'string') next[field.key] = value.trim() ? [value.trim()] : [];
  });
  return next;
};

/**
 * Whether a Property Features field must be answered for this profile.
 *
 * @param {object} field - A field from MLS_SECTIONS.
 * @param {object} profile - The listing's mls_profile.
 * @param {{isVacantLand?: boolean}} [options]
 * @returns {boolean}
 */
export const isMlsFieldRequired = (field, profile, { isVacantLand = false } = {}) => {
  const section = MLS_SECTIONS.find((s) => s.fields.includes(field));
  if (isVacantLand && section?.notForLand) return false;
  if (field.optional) return false;
  if (field.optionalWhen && field.optionalWhen(profile || {})) return false;
  if (field.alternative && isMlsValueFilled(profile?.[field.alternative])) return false;
  return true;
};

/**
 * The required Property Features fields still blank, in form order.
 *
 * @param {object} profile - The listing's mls_profile.
 * @param {{isVacantLand?: boolean}} [options]
 * @returns {Array<{key: string, label: string, section: string}>}
 */
export const missingMlsFields = (profile, options = {}) =>
  MLS_SECTIONS.flatMap((section) =>
    section.fields
      .filter((field) => isMlsFieldRequired(field, profile, options))
      .filter((field) => !isMlsValueFilled(profile?.[field.key]))
      .map((field) => ({
        key: field.key,
        label: field.label || toLabel(field.key),
        section: section.key,
      })),
  );

/**
 * The next selection after a seller clicks a multi-select chip. "None" is
 * exclusive of every other choice, and a field with `maxSelections` ignores
 * a new pick once it is full.
 *
 * @param {object} field - A multiselect field from MLS_SECTIONS.
 * @param {string[]} selected - The current selection.
 * @param {string} option - The chip clicked.
 * @returns {string[]}
 */
export const toggleMultiselectOption = (field, selected, option) => {
  if (selected.includes(option)) return selected.filter((v) => v !== option);
  if (option === NONE_OPTION) return [NONE_OPTION];
  const rest = selected.filter((v) => v !== NONE_OPTION);
  if (field.maxSelections && rest.length >= field.maxSelections) return selected;
  return [...rest, option];
};
